import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  updateStructureComponent,
} from "../../../../../store/reducers/builderReducers/structure/structureActions";

import {
  TextEditorSelectStyle,
  DefaultReactSelectStyle,
} from "../../../ReactSelectStyle/ReactSelectStyle";
import Select from "react-select";

import { Add, Clear, Delete, Edit, Search } from "@mui/icons-material";
import { useAppSelector } from "../../../../../store/hooks";
import {
  NavItemComponent,
  Link,
  ButtonComponent,
  IframeComponent,
} from "../../../../_default/interfaces/base";
import { Pages } from "../../../../../store/reducers/builderReducers/pages/pagesApi";
import Immutable, { Set, Map } from "immutable";
import { Template } from "../../../../../store/reducers/builderReducers/templates/templateApi";
import {
  getTemplateLinkedComponent,
  resolveTemplateAttachId,
  getTemplateSourceComponent,
  templateMatchesLinkStoredId,
} from "../../../../../store/reducers/builderReducers/templates/templateHelpers";
import StructureAPI from "../../../../../store/reducers/builderReducers/structure/structureApi";
import {
  languageCodeFromActiveLanguage,
  languageCodesEqual,
} from "../../../../../utils/languageCode";
import {
  updateActiveEditor,
} from "../../../../../store/reducers/builderReducers/editor/editorActions";
import { Spinner } from "react-bootstrap";
import { getComponentFromStructureMap } from "../../../../_default/utilities/componentIdentity";
import { getLegacyWebpageIdForStructure } from "../../../../../store/reducers/builderReducers/pages/pageIdentity";
import type { Page } from "../../../../../store/reducers/builderReducers/pages/pagesTypes";
import { useShoppingCart } from "../../../../_default/context/ShoppingCartContext";
import { getCatalogProductDisplayFields } from "../../../../_default/utilities/catalogProductAdapter";
import usePageLanguage from "../../../../_default/hooks/usePageLanguage";
import { resolvePersistedLinkUrl } from "../../../../_default/utilities/linkHref";

interface Props {
  id: string | number;
  triggerResize: () => void;
}

interface DropdownItem {
  target: string;
  text: string;
  type: string;
  url: string;
  pageUuid?: string;
}

export default function LinkMenu({ id, triggerResize }: Props) {
  const component = useAppSelector(
    (state) => getComponentFromStructureMap(state.builder.structure.components, id)
  )! as NavItemComponent & ButtonComponent & IframeComponent;
  const webpages = useAppSelector(
    (state) => state.builder.pages.list as Pages["list"]
  );

  const [templateLoading, setTemplateLoading] = useState(false);

  const structure = useAppSelector((state) => state.builder.structure);

  const allTemplates = useAppSelector((state) => state.builder.templates);
  const resolvePageUuidFromLink = (link: any): string | null => {
    if (!link) return null;

    const directUuid =
      link.pageUuid ?? link.page_uuid ?? link.webpageUuid ?? link.webpage_uuid;
    if (typeof directUuid === "string" && directUuid.trim() !== "") {
      return directUuid.trim();
    }

    const legacyRef = link.pageId ?? link.page_id;
    if (typeof legacyRef === "number" && Number.isFinite(legacyRef)) {
      return webpages.find((page) => page.id === legacyRef)?.uuid ?? null;
    }
    if (typeof legacyRef === "string" && legacyRef.trim() !== "") {
      const normalized = legacyRef.trim();
      const asNumber = Number(normalized);
      if (Number.isFinite(asNumber) && String(asNumber) === normalized) {
        return webpages.find((page) => page.id === asNumber)?.uuid ?? null;
      }
      return normalized;
    }

    return null;
  };

  const templates: Set<Map<string, any>> = Immutable.Set(
    useAppSelector((state) => state.builder.templates).map(
      (template: Template) => Immutable.Map(template)
    )
  );

  // Check if templates is not empty and is a valid Immutable.js Set
  if (
    !templates ||
    !templates.forEach ||
    typeof templates.forEach !== "function"
  ) {
    return null; // or handle accordingly if templates is not valid
  }

  // Filter templates based on the "use" property equal to "megaMenu"
  const megaMenuTemplates = templates.filter((template) => {
    if (template.get("use") === "megaMenu")
      return {
        label: template.get("name"),
        value: template.get("name"),
        component: template.get("component"),
      };
  });

  const [dropdownItems, setDropdownItems] = useState<any[] | null>(
    component.attributes.links ?? []
  );

  const [expanded, setExpanded] = useState(true);
  const [webpagesList, setWebpagesList] = useState(
    webpages.map((x) => {
      return { label: x.name, value: x.url, pageUuid: x.uuid };
    })
  );

  const languages = useAppSelector((state) => state.builder.languageSettings);

  const groupedOptions: any[] = [];

  let cachedIFrameURLvalue = "";

  if (component.type === "iframe") {
    cachedIFrameURLvalue = component.attributes.url!;
  }

  languages.activeLanguages.map((language) => {
    const filteredWebpages = webpages.filter((page) => {
      return languageCodesEqual(
        page.languageCode,
        languageCodeFromActiveLanguage(language)
      );
    });

    groupedOptions.push({
      label: languageCodeFromActiveLanguage(language),
      options: filteredWebpages
        .map((page) => {
          return { label: page.name, value: page.url, pageUuid: page.uuid };
        })
        .sort((a, b) => {
          return a.label.localeCompare(b.label);
        }),
    });
  });

  const [url, setUrl] = useState<null | string>(
    component.attributes.link?.url === "" ? "/" : component.attributes.link?.url
  );
  const [urlPageUuid, setUrlPageUuid] = useState<string | null>(
    resolvePageUuidFromLink(component.attributes.link)
  );
  const [target, setTarget] = useState<null | string>(
    component.attributes.link?.target
  );
  const [type, setType] = useState<null | string>(
    component.attributes.link?.type
  );
  const [buttonType, setButtonType] = useState<null | string>(
    component.attributes.type
  );
  const { listableCatalogProducts } = useShoppingCart();
  const lang = usePageLanguage();
  const [productUuid, setProductUuid] = useState<string>(
    component.attributes.productUuid ?? ""
  );
  const [productSearchQuery, setProductSearchQuery] = useState<string>(
    component.attributes.productName ?? ""
  );
  const [isProductSearching, setIsProductSearching] = useState(false);
  const productSearchRef = useRef<HTMLInputElement>(null);
  const productSearchContainerRef = useRef<HTMLDivElement>(null);
  const productSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const productOptions = useMemo(
    () =>
      listableCatalogProducts
        .map((product) => ({
          value: product.productUuid,
          label:
            getCatalogProductDisplayFields(product, lang).displayName ||
            product.productUuid,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [listableCatalogProducts, lang]
  );

  const filteredProductOptions = useMemo(() => {
    const query = productSearchQuery.trim().toLowerCase();
    if (!query) {
      return productOptions;
    }
    return productOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [productOptions, productSearchQuery]);
  const selectedPageOption =
    groupedOptions
      .flatMap((group) => group.options ?? [])
      .find((option) => option.pageUuid === urlPageUuid) ?? null;

  const editor = useAppSelector((state) => state.builder.editor);
  const activePageUuid = useAppSelector((state) => state.builder.pages.active);
  const pagesListForStructure = useAppSelector(
    (state) => state.builder.pages.list
  ) as Page[];
  const structureNumericId = useMemo(
    () =>
      getLegacyWebpageIdForStructure(
        pagesListForStructure,
        activePageUuid as string | null
      ),
    [pagesListForStructure, activePageUuid]
  );

  const [selectedMegaMenuTopPosition, setSelectedMegaMenuTopPosition] =
    useState<any>(
      component.attributes.link?.top_position
        ? component.attributes.link?.top_position
        : "0"
    );

  const [selectedMegaMenuLeftPosition, setSelectedMegaMenuLeftPosition] =
    useState<any>(
      component.attributes.link?.left_position
        ? component.attributes.link?.left_position
        : "0"
    );

  const [megaMenuActivated, setMegaMenuActivated] = useState<string>(
    component.attributes.link?.activate_type
      ? component.attributes.link?.activate_type
      : "hover"
  );

  const [template, setTemplate] = useState<number | string | null>(
    component.attributes.link?.template_id != null
      ? component.attributes.link.template_id
      : null
  );

  const [keepUrlParams, setKeepUrlParams] = useState<0 | 1>(
    component.attributes.keepUrlParams ?? 0
  );

  const refType = useRef(type);
  const refUrl = useRef(url);
  const reftarget = useRef(target);
  const refbuttonType = useRef(buttonType);
  const refUrlPageUuid = useRef<string | null>(urlPageUuid);
  const refDropdownItems = useRef<DropdownItem[] | null>(dropdownItems);
  const refTemplate = useRef(template);
  const refTopPosition = useRef(selectedMegaMenuTopPosition);
  const refLeftPosition = useRef(selectedMegaMenuLeftPosition);
  const refActivateType = useRef(megaMenuActivated);
  const refKeepUrlParams = useRef<0 | 1>(keepUrlParams);
  const refProductUuid = useRef(productUuid);
  const refProductName = useRef(productSearchQuery);

  useEffect(() => {
    const uuid = component.attributes.productUuid;
    if (!uuid) {
      return;
    }
    const found = productOptions.find((x) => x.value === uuid);
    if (found) {
      setProductUuid(uuid);
      setProductSearchQuery(found.label);
      refProductUuid.current = uuid;
      refProductName.current = found.label;
    } else if (component.attributes.productName) {
      setProductSearchQuery(component.attributes.productName);
      refProductName.current = component.attributes.productName;
    }
  }, [component.attributes.productUuid, component.attributes.productName, productOptions]);

  const persistAddToCartProduct = (
    uuid: string | null,
    name: string | null
  ) => {
    refProductUuid.current = uuid ?? "";
    refProductName.current = name ?? "";
    updateStructureComponent(id, "attributes", {
      ...component.attributes,
      type: "add_to_cart",
      buttonType: "button",
      productUuid: uuid,
      productName: name,
      link: {
        type: "None",
        url: "",
        target: "_self",
      },
    });
    triggerResize();
  };

  const closeProductSearch = useCallback(() => {
    setIsProductSearching(false);
    triggerResize();
  }, [triggerResize]);

  useEffect(() => {
    if (!isProductSearching) {
      return;
    }

    const handlePointerDownOutside = (event: MouseEvent) => {
      const root = productSearchContainerRef.current;
      if (!root || root.contains(event.target as Node)) {
        return;
      }
      closeProductSearch();
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
    };
  }, [isProductSearching, closeProductSearch]);

  const handleProductSearchFocus = () => {
    setIsProductSearching(true);
    triggerResize();
  };

  const handleProductInputWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    productSearchRef.current?.focus();
  };

  const handleProductSearchBlur = () => {
    window.setTimeout(() => {
      const root = productSearchContainerRef.current;
      if (root && !root.contains(document.activeElement)) {
        closeProductSearch();
      }
    }, 0);
  };

  const handleProductSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setProductSearchQuery(value);
    refProductName.current = value;
    setIsProductSearching(true);

    if (productUuid) {
      setProductUuid("");
      refProductUuid.current = "";
    }

    if (productSearchTimeout.current) {
      clearTimeout(productSearchTimeout.current);
    }

    productSearchTimeout.current = setTimeout(() => {
      triggerResize();
    }, 300);
  };

  const handleSelectProduct = (option: { value: string; label: string }) => {
    setProductUuid(option.value);
    setProductSearchQuery(option.label);
    setIsProductSearching(false);
    persistAddToCartProduct(option.value, option.label);
  };

  const handleClearProductSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProductUuid("");
    setProductSearchQuery("");
    setIsProductSearching(false);
    persistAddToCartProduct(null, null);
  };

  // React.useEffect(() => {
  //   refType.current = type ? type : refType.current;
  //   refUrl.current = url ? url : refUrl.current;
  //   reftarget.current = target ? target : reftarget.current;
  //   refbuttonType.current = buttonType ? buttonType : refbuttonType.current;
  //   refUrlPageId.current = urlPageId ? urlPageId : refUrlPageId.current;
  //   refDropdownItems.current = dropdownItems
  //     ? dropdownItems
  //     : refDropdownItems.current;
  // }, [type, url, target, buttonType, urlPageId, dropdownItems]);

  useEffect(() => {
    refType.current = type ? type : refType.current;
  }, [type]);

  useEffect(() => {
    if (url === "") {
      refUrl.current = "";
    } else if (url !== "") {
      refUrl.current = url;
    } else {
      refUrl.current = refUrl.current;
    }

    //refUrl.current = url !== "" ? url : refUrl.current;
  }, [url]);

  useEffect(() => {
    refTemplate.current = template ? template : refTemplate.current;
  }, [template]);

  useEffect(() => {
    refTopPosition.current = selectedMegaMenuTopPosition
      ? selectedMegaMenuTopPosition
      : refTopPosition.current;
  }, [selectedMegaMenuTopPosition]);

  useEffect(() => {
    refActivateType.current = megaMenuActivated
      ? megaMenuActivated
      : refActivateType.current;
  }, [megaMenuActivated]);

  useEffect(() => {
    refLeftPosition.current = selectedMegaMenuLeftPosition
      ? selectedMegaMenuLeftPosition
      : refLeftPosition.current;
  }, [selectedMegaMenuLeftPosition]);

  useEffect(() => {
    reftarget.current = target ? target : reftarget.current;
  }, [target]);

  useEffect(() => {
    refTopPosition.current = selectedMegaMenuTopPosition
      ? selectedMegaMenuTopPosition
      : refTopPosition.current;
  }, [selectedMegaMenuTopPosition]);

  useEffect(() => {
    refbuttonType.current = buttonType ? buttonType : refbuttonType.current;
  }, [buttonType]);

  useEffect(() => {
    refUrlPageUuid.current = urlPageUuid;
  }, [urlPageUuid]);

  useEffect(() => {
    if (!dropdownItems || dropdownItems.length === 0) return;

    let changed = false;
    const migratedItems = dropdownItems.map((item) => {
      if (item.type !== "Page" || item.pageUuid) {
        return item;
      }
      const migratedUuid = resolvePageUuidFromLink(item);
      if (!migratedUuid) {
        return item;
      }
      changed = true;
      return { ...item, pageUuid: migratedUuid };
    });

    if (changed) {
      setDropdownItems(migratedItems);
    }
  }, [dropdownItems, webpages]);

  useEffect(() => {
    refDropdownItems.current = dropdownItems
      ? dropdownItems
      : refDropdownItems.current;
  }, [dropdownItems]);

  useEffect(() => {
    refKeepUrlParams.current = keepUrlParams;
  }, [keepUrlParams]);

  useEffect(() => {
    refProductUuid.current = productUuid;
  }, [productUuid]);

  useEffect(() => {
    refProductName.current = productSearchQuery;
  }, [productSearchQuery]);

  useLayoutEffect(() => {
    return () => closure();
  }, []);

  const closure = () => {
    if (
      component.type === "Button" &&
      refbuttonType.current === "add_to_cart"
    ) {
      updateStructureComponent(id, "attributes", {
        ...component.attributes,
        type: "add_to_cart",
        buttonType: "button",
        productUuid: refProductUuid.current || null,
        productName: refProductName.current || null,
        link: {
          type: "None",
          url: "",
          target: "_self",
        },
      });
      triggerResize();
      return;
    }

    if (refType.current === "Dropdown") {
      const pageUrl = webpages.find((x) => x.uuid === refUrlPageUuid.current)?.url;

      updateStructureComponent(id, "attributes", {
        ...component.attributes,
        links: refDropdownItems.current,
        link: {
          pageUuid: refUrlPageUuid.current,
          url: resolvePersistedLinkUrl(
            refType.current,
            refUrl.current,
            pageUrl
          ),
          type: refType.current,
          target: reftarget.current,
        },
        ...((component.type === "Button" ||
          component.type === "nav-item" ||
          component.type === "image" ||
          component.type === "section" ||
          component.type === "logo") && {
          keepUrlParams: refKeepUrlParams.current,
        }),
      });

      triggerResize();
    } else if (refType.current === "megaMenu") {
      const pageUrl = webpages.find((x) => x.uuid === refUrlPageUuid.current)?.url;

      updateStructureComponent(id, "attributes", {
        ...component.attributes,
        link: {
          template_id: refTemplate.current,
          pageUuid: refUrlPageUuid.current,
          url: resolvePersistedLinkUrl(
            refType.current,
            refUrl.current,
            pageUrl
          ),
          type: refType.current,
          target: reftarget.current,
          top_position: refTopPosition.current,
          left_position: refLeftPosition.current,
          activate_type: refActivateType.current,
        },
        ...(component.type === "Button" && { type: refbuttonType.current }),
        ...((component.type === "Button" ||
          component.type === "nav-item" ||
          component.type === "image" ||
          component.type === "section" ||
          component.type === "logo") && {
          keepUrlParams: refKeepUrlParams.current,
        }),
        template_id: template,
      });

      // StructureAPI.addExistingComponent(id, {
      //   id: refTemplate.current!,
      // })

      // if (templateId.toJS()[0].webpage_component_id) {
      //   StructureAPI.addExistingComponent(id, {
      //     id: templateId?.toJS()[0].webpage_component_id,
      //     parent_id: id ?? null,
      //     sort_order: 0,
      //   }).then((x) => {

      //   });
      // }
    } else {
      if (
        (refType.current || buttonType) &&
        refbuttonType.current !== "add_to_cart"
      ) {
        const pageUrl = webpages.find(
          (x) => x.uuid === refUrlPageUuid.current
        )?.url;

        const tplRow = allTemplates.find((t) =>
          templateMatchesLinkStoredId(t, template)
        );
        const tplAttachId = tplRow
          ? resolveTemplateAttachId(tplRow, structure.components)
          : undefined;

        const buttonAttrs =
          component.type === "Button"
            ? {
                type: refbuttonType.current,
                buttonType:
                  refbuttonType.current === "submit" ? "submit" : "button",
              }
            : {};

        updateStructureComponent(id, "attributes", {
          ...component.attributes,
          link: {
            template_id: refTemplate.current,
            pageUuid: refUrlPageUuid.current,
            url: resolvePersistedLinkUrl(
              refType.current,
              refUrl.current,
              pageUrl
            ),
            type: refType.current,
            target: reftarget.current,
            top_position: refTopPosition.current,
            left_position: refLeftPosition.current,
            activate_type: refActivateType.current,
          },
        ...buttonAttrs,
        ...((component.type === "Button" ||
          component.type === "nav-item" ||
          component.type === "image" ||
          component.type === "section" ||
          component.type === "logo") && {
            keepUrlParams: refKeepUrlParams.current,
          }),
          template_id: template,
        });

        if (tplRow && getTemplateLinkedComponent(structure.components, tplRow)) {
          return;
        } else if (tplAttachId != null) {
          StructureAPI.addExistingComponent(structureNumericId!, {
            id: tplAttachId,
            parent_id: (typeof id === "number" ? id : null) as number | null,
            sort_order: 0,
          }).then((x) => {});

          // pages.list.forEach((page) => {
          //   if (page.page_type === "templates") {
          //     return;
          //   } else {
          //     StructureAPI.addExistingComponent(page.id, {
          //       id: templateId.toJS()[0].webpage_component_id,
          //       parent_id: id ?? null,
          //       sort_order: 0,
          //     }).then((x) => {

          //       return;
          //     });
          //   }
          // });
          // StructureAPI.addExistingComponent(pages.active!, {
          //   id: templateId.toJS()[0].webpage_component_id,
          //   parent_id: id ?? null,
          //   sort_order: 0,
          // }).then((x) => {

          // });
        }

        // if (components.get(id)) {

        // }

        triggerResize();
      }
    }
  };

  useEffect(() => {
    // Trigger the resize
    triggerResize();
  }, []);

  function addDropdownItem() {
    const newItem = {
      target: "_blank",
      text: "",
      type: "Web",
      url: "",
    };

    if (!dropdownItems) {
      setDropdownItems([newItem]);
    } else {
      setDropdownItems([...dropdownItems, newItem]);
    }

    // updateStructureComponent(id, "attributes.links", [
    //   ...dropdownItems,
    //   { url: "www.google.ee", type: "Web", target: "_blank" },
    // ]);
  }

  function updateDropdownItem(
    i: number,
    params: Link & { text: string; pageUuid?: string }
  ) {
    if (!dropdownItems) return;

    var dropdownItems_ = [...dropdownItems];
    dropdownItems_[i] = { ...dropdownItems_[i], ...params };
    // updateStructureComponent(id, "attributes.links", dropdownItems_);

    setDropdownItems(dropdownItems_);
  }

  function deleteDropdownItem(i: number) {
    if (!dropdownItems) return;

    var dropdownItems_ = [...dropdownItems];
    dropdownItems_.splice(i, 1);
    // updateStructureComponent(id, "attributes.links", dropdownItems_);

    setDropdownItems(dropdownItems_);
  }

  let types = [
    { label: "Page", value: "Page" },
    { label: "Web in", value: "Web" },
    { label: "Web out", value: "Web_out" },
    { label: "None", value: "None" },
    { label: "Anchor", value: "Anchor" },
    { label: "Phone", value: "Phone" },
    { label: "Email", value: "Email" },
    { label: "Dropdown", value: "Dropdown" },
    { label: "MegaMenu", value: "MegaMenu" },
  ];

  // if (component.type === "nav-item") {
  //   types = [
  //     ...types,
  //     { label: "Anchor", value: "Anchor" },
  //     { label: "Phone", value: "Phone" },
  //     { label: "Email", value: "Email" },
  //     { label: "Dropdown", value: "Dropdown" },
  //     { label: "MegaMenu", value: "MegaMenu" },
  //   ];
  // }

  const linkOpen = [
    { label: "Open new tab", value: "_blank" },
    { label: "Open same tab", value: "_self" },
  ];

  const megaMenuTemplatesList: {
    label: string;
    value: string;
    id: string | null;
    rootComponentUuid: string | null;
    component?: unknown;
  }[] = megaMenuTemplates
    .toJS()
    .map((x) => {
      if (x.use === "megaMenu") {
        const rootId = x.rootComponentUuid?.trim() ?? null;
        return {
          label: x.name,
          value: x.name,
          id: rootId,
          rootComponentUuid: rootId,
          component: x.component,
          ...x,
        };
      }
      return { label: "", value: "", id: null, rootComponentUuid: null };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  // const megaMenuTemplates = [
  //   templates.map((x) => {
  //     if (x.use === "megaMenu") {
  //       return { label: x.name, value: x.name };
  //     } else {
  //       return;
  //     }
  //   }),
  // ];

  // const MegaMenuTemplates = [
  //   templates.map((x) => {
  //     if (x.use === "megaMenu") {
  //       return { label: x.name, value: x.name };
  //     } else {
  //       return;
  //     }
  //   }),
  // ];

  let widthTypes = [
    {
      value: "px",
      label: "px",
    },
  ];

  let activateMenu = [
    {
      label: "hover",
      value: "hover",
    },
    {
      label: "click",
      value: "click",
    },
  ];

  const buttonTypes = [
    { label: "Button", value: "button" },
    { label: "Submit", value: "submit" },
    { label: "Add to cart", value: "add_to_cart" },
  ];

  const supportsLinkValueInput =
    (component.type === "Button" && buttonType === "button") ||
    component.type === "nav-item" ||
    component.type === "image" ||
    component.type === "section" ||
    component.type === "logo";

  const groupStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0px",
    margin: "0px",
  };

  async function setTemplateActive(e: any, template_: Template) {
    e.stopPropagation();
    setTemplateLoading(true);
    const source = getTemplateSourceComponent(template_);
    if (!source) {
      setTemplateLoading(false);
      return;
    }
    updateActiveEditor({
      ...editor,
      component_uuid: source.uuid?.trim() || null,
      component_id: source.id ?? null,
      component_parent_uuid: null,
      component_parent_id: null,
      template: source,
    });
    setTemplateLoading(false);

    // Update the editor

    // if (inClass("dropdown-trigger", e.target)) return;
    // if (template_.component!.id === editorComponent?.parent_id) {
    //   return;
    // } else if (pages.list.find((x) => x.id === template_.webpages[0])) {
    //   if (!template_.webpages.includes(pages.active!)) {
    //     setActivePage(
    //       pages.list.find((x) => x.id === template_.webpages[0])!.id
    //     );
    //     // Wait for the new components to load
    //     await new Promise((resolve) => setTimeout(resolve, 1000));
    //   }
    //   if (template_.component!.id !== editorComponent?.parent_id) {
    //     if (editorComponent) {
    //       deleteComponentById(editorId);
    //     }
    //     addNewComponent({
    //       id: editorId,
    //       type: "editor",
    //       sort_order: 1,
    //       parent_id: template_.component!.id,
    //       childElements: [],
    //     } as any);
    //     setEditableComponentId(template_.component!.id);
    //   }
    // } else {
    //   setEditTemplate(template_);
    // }
  }

  const formatGroupLabel = (data: any) => (
    <div style={groupStyles}>
      <span style={{ fontSize: "10px", color: "#e7e7e7", fontWeight: "bold" }}>
        {data.label}
      </span>
      <span
        style={{
          fontSize: "10px",
          color: "#e7e7e7",
          fontWeight: "bold",
          backgroundColor: "#2e2e2e",
          padding: "3px 5px",
          borderRadius: "2px",
        }}
      >
        {data.options.length}
      </span>
    </div>
  );

  return (
    <div className="b-gap-3">
      <div
        className={"  b-gap-2"}
        style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}
      >
        {component.type === "iframe" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span>URL:</span>
            <input
              value={cachedIFrameURLvalue}
              onChange={(e) => {
                if (e.target.value) {
                  cachedIFrameURLvalue = e.target.value;

                  updateStructureComponent(
                    id,
                    "attributes.url",
                    e.target.value
                  );
                } else if (e.target.value === "") {
                  cachedIFrameURLvalue = e.target.value;
                  updateStructureComponent(id, "attributes.url", "");
                }
              }}
              style={{
                background: "transparent",
                border: "none",
                borderBottom: "1px solid white",
                color: "#fff",
              }}
            />
          </div>
        )}
        {component.type === "Button" && (
          <div>
            <Select
              onChange={(e) => {
                const nextType = e!.value;
                setButtonType(nextType);
                refbuttonType.current = nextType;

                if (nextType === "submit") {
                  setType(null);
                  setTarget(null);
                  setUrl(null);
                  updateStructureComponent(id, "attributes", {
                    ...component.attributes,
                    type: "submit",
                    buttonType: "submit",
                  });
                } else if (nextType === "add_to_cart") {
                  setType(null);
                  setTarget(null);
                  setUrl(null);
                  updateStructureComponent(id, "attributes", {
                    ...component.attributes,
                    type: "add_to_cart",
                    buttonType: "button",
                    link: {
                      type: "None",
                      url: "",
                      target: "_self",
                    },
                  });
                } else {
                  setType("Page");
                  setTarget("_self");
                  updateStructureComponent(id, "attributes", {
                    ...component.attributes,
                    type: "button",
                    buttonType: "button",
                  });
                }
                triggerResize();
              }}
              isSearchable={false}
              className="m-0 link-menu_input"
              styles={{
                ...TextEditorSelectStyle,
                control: (base) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }),
                container: (base) => ({
                  ...base,
                  minWidth: "80px",
                }),
              }}
              noOptionsMessage={({}) => "Select type"}
              value={buttonTypes.find((x) => x.value === buttonType)}
              options={buttonTypes}
              placeholder="Select type"
            ></Select>
          </div>
        )}
        {component.type === "Button" && buttonType === "add_to_cart" && (
          <div
            ref={productSearchContainerRef}
            className="link-menu_product-search position-relative"
          >
            <div
              className="content-width-menu_input link-menu_product-search__input d-flex align-items-center"
              style={{
                borderRadius: "4px",
                padding: "2px 4px",
                cursor: "pointer",
              }}
              onClick={handleProductInputWrapperClick}
            >
              <input
                ref={productSearchRef}
                type="text"
                value={productSearchQuery}
                onChange={handleProductSearchChange}
                onFocus={handleProductSearchFocus}
                onBlur={handleProductSearchBlur}
                placeholder={productUuid ? "" : "Search product by name"}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  width: "100%",
                  outline: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
              {productUuid ? (
                <Clear
                  style={{
                    fontSize: "16px",
                    marginLeft: "4px",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  onClick={handleClearProductSelection}
                />
              ) : (
                <Search
                  style={{
                    fontSize: "16px",
                    marginLeft: "4px",
                    flexShrink: 0,
                  }}
                />
              )}
            </div>

            {isProductSearching && (
              <div
                className="link-menu_product-search__dropdown"
                style={{
                  position: "absolute",
                  width: "100%",
                  maxHeight: "200px",
                  overflowY: "auto",
                  top: "24px",
                  left: 0,
                  background: "#323232",
                  borderRadius: "2px",
                  zIndex: 1000,
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
              >
                {filteredProductOptions.length > 0 ? (
                  filteredProductOptions.map((option) => (
                    <div
                      key={option.value}
                      className={
                        "dropdown-item builder-dropdown__item" +
                        (productUuid === option.value
                          ? " builder-dropdown__item--active"
                          : "")
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectProduct(option);
                      }}
                    >
                      {option.label}
                    </div>
                  ))
                ) : productSearchQuery ? (
                  <div className="dropdown-item builder-dropdown__item">
                    No products found
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
        {((component.type === "Button" &&
          buttonType !== "add_to_cart" &&
          (buttonType === "button" || buttonType === "submit")) ||
          component.type === "nav-item" ||
          component.type === "image" ||
          component.type === "section" ||
          component.type === "logo") && (
          <Select
            onChange={(e) => {
              setType(e!.value);
              if (e!.value === "Page") {
                setUrl(null);
              } else if (type === "Page") {
                setUrl("");
              } else {
                setUrl(null);
              }
            }}
            isSearchable={false}
            className="m-0 link-menu_input"
            styles={{
              ...TextEditorSelectStyle,
              container: (base) => ({
                ...base,
                minWidth: "80px",
              }),

              control: (base) => ({
                ...base,
                backgroundColor: "transparent",
                border: 0,
                minHeight: "inherit",
                borderRadius: 0,
                boxShadow: "none",
                width: "100%",
                display: "flex",
              }),

              singleValue: (base) => ({
                ...base,
                textAlign: "left",
                color: "#e7e7e7",
              }),
            }}
            noOptionsMessage={({}) => ""}
            value={
              types.find((x) => x.value === type)
                ? types.find((x) => x.value === type)
                : types.find((x) => x.value === "None")
            }
            options={
              buttonType === "submit"
                ? types.filter((type) =>
                    ["Dropdown", "MegaMenu", "None"].includes(type.value)
                  )
                : types
            }
            placeholder=""
          ></Select>
        )}
        {/* {type === "Page" && (
          <Select
            onChange={(e) => {
              setUrlPageUuid(e!.pageUuid);
              setUrl(e!.value);
            }}
            isSearchable={false}
            className="m-0 link-menu_input"
            styles={TextEditorSelectStyle}
            noOptionsMessage={({}) => ""}
            value={webpagesList.find((x) => x.value === url)}
            options={webpagesList}
            placeholder=""
          ></Select>
        )} */}
        {type === "MegaMenu" && (
          <>
            <div className="link-menu_input">
              <Select
                onChange={(e) => {
                  setTemplate(e!.id);

                  // setUrlPageId(e!.pageId);
                  // setUrl(e!.value);
                }}
                isSearchable={false}
                className="m-0"
                styles={{
                  ...TextEditorSelectStyle,

                  container: (base) => ({
                    ...base,
                    width: "auto",
                  }),

                  option: (styles, data) => ({
                    ...DefaultReactSelectStyle.option!(styles, data),
                    textAlign: "left",

                    padding: "2px 12px",
                  }),
                }}
                noOptionsMessage={({}) => ""}
                value={
                  megaMenuTemplatesList.find((opt) =>
                    templateMatchesLinkStoredId(opt, template)
                  ) ?? megaMenuTemplatesList[0]
                }
                options={megaMenuTemplatesList}
                placeholder=""
                formatGroupLabel={formatGroupLabel}
              ></Select>
              {template && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    textTransform: "uppercase",
                  }}
                  onClick={(e) => {
                    if (template) {
                      const templateComponent = allTemplates.find((x) =>
                        templateMatchesLinkStoredId(x, template)
                      );

                      if (!templateComponent) return;

                      setTemplateActive(e, templateComponent);
                    }
                  }}
                >
                  {templateLoading ? (
                    <Spinner
                      style={{ fontSize: "14px", marginLeft: "5px" }}
                      size="sm"
                    />
                  ) : (
                    <Edit style={{ fontSize: "14px", marginLeft: "5px" }} />
                  )}
                </div>
              )}

              {/* SHow only if template is selected */}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
              onClick={(e) => {
                const templatesButton = document.getElementById(
                  "toggleTemplatesMenu"
                );

                if (!templatesButton) return;

                templatesButton.click();

                const AddButton = document.getElementById(
                  "templates-add-button"
                );
              }}
            >
              <Add
                style={{
                  fontSize: "15px",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
              <span style={{ marginRight: "5px", marginLeft: "5px" }}>
                Top:
              </span>

              <div style={{ display: "flex", gap: "5px" }}>
                <input
                  type="number"
                  className="global-editor__input builder-input spacing-menu_input"
                  size={1}
                  value={selectedMegaMenuTopPosition}
                  onChange={(ev) => {
                    // if (ev.target.value === "" || ev.target.value === "0") {
                    //   setSelectedWidth("px");
                    //   return;
                    // // }

                    // if (ev.target.value === "0") return;

                    if (ev.target.value === "") {
                      setSelectedMegaMenuTopPosition("");

                      updateStructureComponent(
                        id,
                        "attributes.link.top_position",
                        "0"
                      );

                      updateStructureComponent(
                        id,
                        "attributes.link.top_position_unit",
                        "px"
                      );
                    } else {
                      setSelectedMegaMenuTopPosition(ev.target.value);

                      updateStructureComponent(
                        id,
                        "attributes.link.top_position",
                        ev.target.value
                      );

                      updateStructureComponent(
                        id,
                        "attributes.link.top_position_unit",
                        "px"
                      );
                    }
                  }}
                />

                <Select
                  menuPosition="fixed"
                  className="m-0 button-menu_input"
                  isSearchable={false}
                  styles={TextEditorSelectStyle}
                  noOptionsMessage={({}) => ""}
                  value={widthTypes.filter((x) => x.value === "px")}
                  onChange={(val) => {}}
                  options={widthTypes.filter((x) => x.value === "px")}
                  placeholder=""
                ></Select>
              </div>

              <span style={{ marginRight: "5px", marginLeft: "5px" }}>
                Left:
              </span>

              <div style={{ display: "flex", gap: "5px" }}>
                <input
                  type="number"
                  className="global-editor__input builder-input spacing-menu_input"
                  size={1}
                  // value={selectedThumbnailMediaWidth}
                  value={selectedMegaMenuLeftPosition}
                  onChange={(ev) => {
                    // if (ev.target.value === "" || ev.target.value === "0") {
                    //   setSelectedWidth("px");
                    //   return;

                    // if (ev.target.value === "0") return;

                    if (ev.target.value === "") {
                      setSelectedMegaMenuLeftPosition("");

                      updateStructureComponent(
                        id,
                        "attributes.link.left_position",
                        "0"
                      );

                      updateStructureComponent(
                        id,
                        "attributes.link.left_position_unit",
                        "px"
                      );
                    } else {
                      setSelectedMegaMenuLeftPosition(ev.target.value);

                      updateStructureComponent(
                        id,
                        "attributes.link.left_position",
                        ev.target.value
                      );

                      updateStructureComponent(
                        id,
                        "attributes.link.left_position_unit",
                        "px"
                      );
                    }
                  }}
                />

                <Select
                  menuPosition="fixed"
                  className="m-0 button-menu_input"
                  isSearchable={false}
                  styles={TextEditorSelectStyle}
                  noOptionsMessage={({}) => ""}
                  value={widthTypes.filter((x) => x.value === "px")}
                  onChange={(val) => {}}
                  options={widthTypes.filter((x) => x.value === "px")}
                  placeholder=""
                ></Select>

                {/* <span
                  style={{
                    marginRight: "5px",
                    marginLeft: "5px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Activate:
                </span>
                <Select
                  onChange={(e) => {
         

                    if (!e) return;

                    setMegaMenuActivated(e.value);

                    updateStructureComponent(
                      id,
                      "attributes.link.activate_type",
                      e.value
                    );

                   
                    // setTemplate(e!.id);
                    // setUrlPageId(e!.pageId);
                    // setUrl(e!.value);
                  }}
                  isSearchable={false}
                  className="m-0 link-menu_input"
                  styles={{
                    ...TextEditorSelectStyle,

                    container: (base) => ({
                      ...base,
                      width: "auto",
                    }),

                    option: (styles, data) => ({
                      ...DefaultReactSelectStyle.option!(styles, data),
                      textAlign: "left",

                      padding: "2px 12px",
                    }),
                  }}
                  noOptionsMessage={({}) => ""}
                  value={activateMenu.find(
                    (x) => x.value === megaMenuActivated
                  )}
                  options={activateMenu}
                  placeholder=""
                  formatGroupLabel={formatGroupLabel}
                ></Select> */}
              </div>
            </div>
          </>
        )}

        {type === "Page" && supportsLinkValueInput && (
          <Select
            onChange={(e) => {
              setUrlPageUuid(e!.pageUuid);
              setUrl(e!.value);
            }}
            isSearchable={false}
            className="m-0 link-menu_input"
            styles={{
              ...TextEditorSelectStyle,

              container: (base) => ({
                ...base,
                width: "100px",
              }),

              option: (styles, data) => ({
                ...DefaultReactSelectStyle.option!(styles, data),
                textAlign: "left",

                padding: "2px 12px",
              }),

              control: (base) => ({
                ...base,
                backgroundColor: "transparent",
                border: 0,
                minHeight: "inherit",
                borderRadius: 0,
                boxShadow: "none",
                width: "100%",
              }),
            }}
            noOptionsMessage={({}) => ""}
            value={selectedPageOption}
            options={groupedOptions}
            placeholder="Select page"
            formatGroupLabel={formatGroupLabel}
          ></Select>
        )}
        {["Anchor", "Phone", "Email"].includes(type!) &&
          supportsLinkValueInput && (
            <input
              className="global-editor__input builder-input link-menu_input web_input_bg"
              style={{ width: "100px" }}
              size={5}
              placeholder={
                type === "Anchor" ? "#" : type === "Phone" ? "tel:" : "mailto:"
              }
              value={url ?? ""}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setUrl(e.target.value);

                triggerResize();
              }}
            />
          )}
        {["Web", "Web_out"].includes(type!) && supportsLinkValueInput && (
          <div style={{ width: "100%" }}>
            <input
              className="global-editor__input builder-input link-menu_input web_input_bg"
              style={{ width: "100px" }}
              size={5}
              placeholder={type === "Web" ? "/" : "www.google.ee"}
              value={url ?? ""}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setUrl(e.target.value);

                triggerResize();
              }}
            />
          </div>
        )}
        {/* {type !== "Dropdown" && (
            <MoreHorizOutlined
              style={{ fontSize: "18px" }}
              onClick={() => {
                setExpanded(!expanded);
                triggerResize();
              }}
            />
          )} */}
        {type === "Dropdown" && (
          <div
            className="hover-underline link-menu_input color-white"
            style={{ color: "#fff!important", width: "50px" }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addDropdownItem();
            }}
          >
            + Add
          </div>
        )}
        {expanded &&
          type !== "Dropdown" &&
          type !== "Anchor" &&
          type !== "Web" &&
          type !== "None" &&
          type !== "Phone" &&
          type !== "Email" &&
          type !== undefined &&
          type !== "MegaMenu" &&
          supportsLinkValueInput && (
            <Select
              onChange={(e) => setTarget(e!.value)}
              isSearchable={false}
              className="m-0 link-menu_input"
              styles={TextEditorSelectStyle}
              noOptionsMessage={({}) => ""}
              value={
                linkOpen.find((x) => x.value === target)
                  ? linkOpen.find((x) => x.value === target)
                  : linkOpen[1]
              }
              options={linkOpen}
              placeholder=""
            ></Select>
          )}
      </div>

      {((component.type === "Button" && buttonType === "button") ||
        component.type === "nav-item" ||
        component.type === "image" ||
        component.type === "section" ||
        component.type === "logo") &&
        (type === "Page" ||
          type === "Web" ||
          type === "Web_out" ||
          type === "Anchor" ||
          type === "Email" ||
          type === "Phone") && (
          <div
            className="link-menu_toggle-row"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const next: 0 | 1 = keepUrlParams ? 0 : 1;
              refKeepUrlParams.current = next;
              setKeepUrlParams(next);
            }}
          >
            <span className="link-menu_toggle-label">Forward URL parameters</span>
            <label className="switch" style={{ marginRight: "5px" }}>
              <input type="checkbox" checked={keepUrlParams === 1} readOnly />
              <span className="slider round"></span>
            </label>
          </div>
        )}

      {type === "Dropdown" && (
        <div
          style={{
            display: "grid",
            flexDirection: "column",
            width: "410px",
            alignItems: "center",
          }}
        >
          {dropdownItems?.map((item, i) => (
            <div className={"dropdown-parent-grid"} key={i}>
              <Select
                onChange={(e) => {
                  const firstPageOption = webpagesList[0];
                  updateDropdownItem(i, {
                    ...item,
                    type: e!.value,
                    url:
                      e!.value === "Page" && firstPageOption
                        ? firstPageOption.value
                        : item.url,
                    pageUuid:
                      e!.value === "Page" && firstPageOption
                        ? firstPageOption.pageUuid
                        : undefined,
                  });
                }}
                isSearchable={false}
                className="m-0 link-menu_input"
                styles={{
                  ...TextEditorSelectStyle,
                  container: (base) => ({
                    ...base,
                    display: "flex",

                    width: "100%",
                    minWidth: "60px",
                  }),
                  option: (styles, data) => ({
                    ...DefaultReactSelectStyle.option!(styles, data),
                    display: "flex",
                    justifyContent: "space-between",
                  }),

                  control: (base) => ({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }),
                }}
                noOptionsMessage={({}) => ""}
                value={types.find((x) => x.value === item.type)}
                options={types.filter((x) => {
                  if (x.value === "Dropdown") return false;
                  if (x.value === "MegaMenu") return false;

                  return true;
                })}
                placeholder=""
              ></Select>
              <input
                className="global-editor__input  link-menu_input link-menu_input_text"
                placeholder="Label"
                size={5}
                value={item.text}
                onChange={(e) =>
                  updateDropdownItem(i, { ...item, text: e.target.value })
                }
              />
              {["Anchor", "Phone", "Email", "Web", "Web_out"].includes(
                item.type
              ) && (
                <input
                  className="global-editor__input builder-input flex-grow-1 link-menu_input"
                  placeholder={
                    item.type === "Anchor"
                      ? "#"
                      : item.type === "Phone"
                      ? "tel:"
                      : item.type === "Email"
                      ? "mailto:"
                      : item.type === "Web"
                      ? "/"
                      : "www.google.ee"
                  }
                  style={{ width: "100%!important" }}
                  size={5}
                  value={item.url}
                  onChange={(e) =>
                    updateDropdownItem(i, { ...item, url: e.target.value })
                  }
                />
              )}
              {item.type === "Page" && (
                <Select
                  onChange={(e) => {
                    const pageUrl = webpages.find(
                      (x) => x.uuid === e!.pageUuid
                    )?.url;

                    updateDropdownItem(i, {
                      ...item,
                      url: pageUrl ? pageUrl : e!.value,
                      pageUuid: e!.pageUuid,
                    });
                  }}
                  maxMenuHeight={150}
                  styles={{
                    ...TextEditorSelectStyle,
                    container: (base) => ({
                      ...base,
                      width: "100%",
                      minWidth: "100px",
                    }),
                  }}
                  isSearchable={false}
                  className="m-0 link-menu_input"
                  noOptionsMessage={({}) => ""}
                  value={webpagesList.find(
                    (x) =>
                      x.pageUuid ===
                      (item.pageUuid ?? resolvePageUuidFromLink(item))
                  )}
                  options={webpagesList}
                  placeholder=""
                ></Select>
              )}

              {/* Do not remove, needed for grid */}
              {item.type === "None" && <div></div>}

              {!["Anchor", "None", "Web"].includes(item.type) ? (
                <Select
                  onChange={(e) =>
                    updateDropdownItem(i, { ...item, target: e!.value })
                  }
                  isSearchable={false}
                  className="m-0 link-menu_input"
                  styles={{
                    ...TextEditorSelectStyle,
                    control: (base) => ({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }),
                    container: (base) => ({
                      ...base,
                      width: "100%",
                      minWidth: "60px",
                    }),
                  }}
                  noOptionsMessage={({}) => ""}
                  value={linkOpen.find((x) => x.value === item.target)}
                  options={linkOpen}
                  placeholder=""
                ></Select>
              ) : (
                <div></div>
              )}
              <span
                className="text-end flex-grow-1 pe-1 fw-bold clickable"
                style={{ fontWeight: "13px", minWidth: "20px" }}
                onClick={() => deleteDropdownItem(i)}
              >
                <Delete />
              </span>
            </div>
          ))}
        </div>
      )}

      {/* <div
        className="link-menu_input"
        style={{
          fontSize: "11px",

          color: "white",
          padding: "5px",
          backgroundColor: "green",
        }}
        onClick={() => {
          closure();
        }}
      >
        SAVE
      </div> */}
    </div>
  );
}
